// Public OSS project reads (arc 101 slice 11, plan doc 05) — the SPA's half of
// `GET /v1/public/oss/:owner/:repo/stats`, the ONE unauthenticated route on the
// versioned surface.
//
// The donate wizard's first step spends it as a VALIDATOR: a slug that isn't a
// live donatable project must fail BEFORE the donor is sent to Stripe, because a
// charge is not reversible by a back button. The route already answers exactly
// that question for the cases it 404s — "unknown", "private", "not enabled"
// and "neither opted in" fold into one indistinguishable answer, on
// purpose (a pre-auth route must not leak which) — so `null` here means "not a
// project you can fund", and the server's gate, not a client check, decides it.
//
// A 200 is not automatically a fundable verdict (#5660): the route also answers
// 200 for an enabled, public repo whose owner has consented to publish its agent-authored
// numbers (the `public_stats_enabled` flag) but has not opted in to donations,
// with a body that carries no donation block. `isDonationStats` checks the block the
// wizard reads, and a body without it resolves `null` too, so the wizard keeps
// reading it as not fundable.
//
// It takes NO bearer: `apiFetch(path, null)` sends no `Authorization` header,
// and the header the SPA does attach (`X-Dz-Account`) is ignored by a route
// mounted ahead of the auth guard. Reading it under a dead session still works,
// which is the point — the same call powers the public badges.

import { RepoFullNameSchema } from '@developerz/domain';
import { ApiError, apiFetch } from './api';

/**
 * Marketing origin that serves the public project pages. Hardcoded like every
 * other www link in the SPA (`ConsentGate`, `Login`, `RepoPolicyTab`) — the
 * dashboard has no site-URL env, and one is not worth minting for a link.
 */
const OSS_SITE_ORIGIN = 'https://www.developerz.ai';

/**
 * A project's DONATION block: one of the three bodies `maskView` returns
 * (`apps/api/src/routes/public-oss.ts`: donation block, `agent_authored`, or
 * both); this type carries the donation half only. The wizard reads only
 * `full_name` (to echo the canonical casing back) and the backer counts (to
 * show the donor what they are joining); the rest rides along because the
 * route serves one body.
 */
export type OssProjectStats = {
  readonly full_name: string;
  readonly window: string;
  readonly issues_this_month: number;
  readonly prs_this_month: number;
  readonly handled_this_month: number;
  readonly handled_all_time: number;
  readonly backers: number;
  readonly ai_devs_backing: number;
};

/**
 * The project's public page on the marketing site — a gift's human address, and
 * the only link the donor table can offer for a repo the donor does NOT own (the
 * dashboard's own `/repos/:full_name` is account-scoped and would 404 for them).
 */
export const ossProjectUrl = (fullName: string): string =>
  `${OSS_SITE_ORIGIN}/oss/${fullName.split('/').map(encodeURIComponent).join('/')}`;

/**
 * Does this read as `owner/repo` at all? The SHARED domain grammar
 * (`RepoFullNameSchema`), so the form accepts exactly the slugs the API does —
 * a second regex here would drift into refusing repos the server takes.
 *
 * Cheap local refusal only: it answers "this can't be a repo", never "this repo
 * accepts donations". That verdict is the server's, below.
 */
export const isRepoFullName = (raw: string): boolean =>
  RepoFullNameSchema.safeParse(raw.trim()).success;

/**
 * Resolve a project's public stats, or `null` when it is not donatable.
 *
 * A 404 is one way to that `null`; a 200 whose body carries no donation block
 * is the other (#5660: the route also answers public-stats repos that did
 * not opt into donations, a body with `full_name` and `agent_authored` only).
 * `isDonationStats` draws that line, so the wizard never reads `backers` off a
 * repo that cannot take a donation.
 *
 * A 404 is an ANSWER, not a failure — the route folds every ineligible case into
 * it — so it resolves rather than throws, and the caller renders "we can't take a
 * donation for that project" instead of an error box with a Retry that would 404
 * forever (the `RepoPendingTab` rule). Everything else (429, 5xx, a dead network)
 * still throws: those are "ask again", and answering them as "no such project"
 * would refuse a real one.
 */
export async function fetchOssProjectStats(fullName: string): Promise<OssProjectStats | null> {
  const slug = fullName.trim();
  const path = `/v1/public/oss/${slug.split('/').map(encodeURIComponent).join('/')}/stats`;
  try {
    const body = await apiFetch<unknown>(path, null);
    return isDonationStats(body) ? body : null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Does this 200 body carry the donation block the wizard reads? Checks the
 * three fields the wizard reads (`full_name`, `backers`, `ai_devs_backing`),
 * not the whole shape. `full_name` rides in the route's OTHER body too (#5660,
 * a public-stats repo that did not opt into donations), so it proves nothing
 * on its own; the backer counts do.
 */
export function isDonationStats(body: unknown): body is OssProjectStats {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as { backers?: unknown; ai_devs_backing?: unknown; full_name?: unknown };
  return (
    typeof b.backers === 'number' &&
    typeof b.ai_devs_backing === 'number' &&
    typeof b.full_name === 'string'
  );
}
